import { ComponentProps, useState } from 'react';
import { Icon, InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@utilities-one/gpm-ui';

import { FormBase } from '@/library/form/components/FormBase';
import { useFieldContext } from '@/library/form/contexts';
import { FormControlProps } from '@/library/form/types/form.type';

type FormPasswordInputProps = FormControlProps & {
  inputProps?: ComponentProps<typeof InputGroupInput>;
  /** Styling hook for the surrounding `InputGroup` surface (border, height, …). */
  groupProps?: ComponentProps<typeof InputGroup>;
};

/**
 * Password field with a show/hide (eye) toggle — the gpm-ui counterpart of the
 * legacy NextUI `PasswordInput`. Built on `InputGroup` so the toggle button
 * sits inside the input surface; the control stays `type='password'` until the
 * user opts into visibility.
 */
export function FormPasswordInput({ inputProps, groupProps, ...props }: FormPasswordInputProps) {
  const field = useFieldContext<string>();
  const [isVisible, setIsVisible] = useState(false);
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  return (
    <FormBase {...props}>
      <InputGroup {...groupProps}>
        <InputGroupInput
          {...inputProps}
          id={field.name}
          name={field.name}
          type={isVisible ? 'text' : 'password'}
          value={field.state.value}
          onBlur={field.handleBlur}
          onChange={(e) => field.handleChange(e.target.value)}
          aria-invalid={isInvalid}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={isVisible ? 'Hide password' : 'Show password'}
            onClick={() => setIsVisible((visible) => !visible)}
          >
            <Icon name={isVisible ? 'eye-off' : 'eye'} />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </FormBase>
  );
}
